import React, { useCallback, useState } from 'react';
import { ListChecks, ChevronRight, Play, RotateCcw, Plus } from 'lucide-react';
import SectionShell from '../plan/SectionShell';

/**
 * PlaceKeepingCounter — long-procedure step tracker.
 *
 * The user (crew leader) sets up a procedure name + newline-separated
 * step list once. Every "Next step" click posts a `place_keeping_step_completed`
 * event, and the derived `current_step_index` bubbles down from the parent.
 *
 * A "Reset" posts `place_keeping_reset` (preserves the audit trail; does
 * NOT delete the setup event).
 */
export default function PlaceKeepingCounter({ performState, onLogged, isReadOnly, activeHoldsCount = 0 }) {
  const pk = performState?.place_keeping || { steps: [], completed_step_indices: [], current_step_index: null, procedure_name: null, total_steps: 0 };
  const isConfigured = (pk.steps || []).length > 0;
  const [nameDraft, setNameDraft] = useState('');
  const [stepsDraft, setStepsDraft] = useState('');
  const [busy, setBusy] = useState(false);

  const setupProcedure = useCallback(async () => {
    const steps = stepsDraft.split('\n').map((s) => s.trim()).filter(Boolean);
    if (!nameDraft.trim() || steps.length === 0) return;
    setBusy(true);
    try {
      await onLogged?.('place_keeping_procedure_set', {
        procedure_name: nameDraft.trim(),
        steps,
      });
      setNameDraft('');
      setStepsDraft('');
    } finally {
      setBusy(false);
    }
  }, [nameDraft, stepsDraft, onLogged]);

  const advance = useCallback(async () => {
    if (pk.current_step_index === null || pk.current_step_index === undefined) return;
    setBusy(true);
    try {
      await onLogged?.('place_keeping_step_completed', {
        step_index: pk.current_step_index,
        step_name:  pk.steps[pk.current_step_index],
      });
    } finally {
      setBusy(false);
    }
  }, [pk.current_step_index, pk.steps, onLogged]);

  const reset = useCallback(async () => {
    setBusy(true);
    try {
      await onLogged?.('place_keeping_reset', {
        procedure_name: pk.procedure_name,
      });
    } finally {
      setBusy(false);
    }
  }, [pk.procedure_name, onLogged]);

  const pct = pk.total_steps ? Math.round((pk.completed_step_indices.length / pk.total_steps) * 100) : 0;

  return (
    <SectionShell
      icon={ListChecks}
      title="Place-Keeping Counter"
      subtitle="Track your position in the procedure step-by-step. Prevents lost place on long or interrupted work."
      color="#67e8f9"
      badge={
        isConfigured
          ? `${pk.completed_step_indices.length}/${pk.total_steps} done`
          : 'Set up'
      }
      testId="jbv2-section-place-keeping"
    >
      {!isConfigured && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div className="jbv2-field">
            <span className="jbv2-field-label">Procedure name</span>
            <input
              type="text"
              className="jbv2-field-input"
              value={nameDraft}
              onChange={(e) => setNameDraft(e.target.value)}
              placeholder="e.g. Switching order 42-A"
              data-testid="jbv2-pk-procedure-name"
              disabled={isReadOnly}
            />
          </div>
          <div className="jbv2-field">
            <span className="jbv2-field-label">Steps (one per line)</span>
            <textarea
              className="jbv2-field-textarea"
              value={stepsDraft}
              onChange={(e) => setStepsDraft(e.target.value)}
              placeholder={'Verify tag on breaker 3-4\nOpen breaker 3-4\nGround load side\nTest dead\n…'}
              rows={6}
              data-testid="jbv2-pk-steps-input"
              disabled={isReadOnly}
            />
            <span className="jbv2-field-help">Leave blank lines out — each newline becomes one step.</span>
          </div>
          {!isReadOnly && (
            <div>
              <button
                type="button"
                className="jbv2-row-btn"
                onClick={setupProcedure}
                disabled={busy || !nameDraft.trim() || !stepsDraft.trim()}
                data-testid="jbv2-pk-setup-btn"
              >
                <Play size={12} /> Start procedure
              </button>
            </div>
          )}
        </div>
      )}

      {isConfigured && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }} data-testid="jbv2-pk-active">
          <div>
            <div className="jbv2-row-cat" data-testid="jbv2-pk-procedure-name-display">
              {pk.procedure_name}
            </div>
            <div className="jbv2-hp-row-meta">
              Progress: {pct}% ({pk.completed_step_indices.length} of {pk.total_steps} steps)
            </div>
            <div style={{
              marginTop: 8, height: 6, borderRadius: 999,
              background: 'rgba(148,163,184,0.14)', overflow: 'hidden',
            }}>
              <div
                data-testid="jbv2-pk-progress-bar"
                style={{
                  height: '100%',
                  width: `${pct}%`,
                  background: 'linear-gradient(90deg, rgba(103,232,249,0.9), rgba(59,130,246,0.9))',
                  transition: 'width 300ms ease',
                }}
              />
            </div>
          </div>

          {pk.steps.map((step, i) => {
            const done = pk.completed_step_indices.includes(i);
            const isCurrent = i === pk.current_step_index;
            return (
              <div
                key={i}
                className={`jbv2-row${done ? ' jbv2-row--dismissed' : ''}`}
                data-testid={`jbv2-pk-step-${i}`}
                style={isCurrent ? { border: '1px solid rgba(103,232,249,0.55)', background: 'rgba(103,232,249,0.08)' } : undefined}
              >
                <div className="jbv2-row-body">
                  <div className="jbv2-row-cat">
                    Step {i + 1}
                    {isCurrent && <span style={{ marginLeft: 8, color: '#67e8f9' }}>— CURRENT</span>}
                  </div>
                  <div className="jbv2-row-note">{step}</div>
                </div>
              </div>
            );
          })}

          {!isReadOnly && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {activeHoldsCount > 0 && (
                <div
                  data-testid="jbv2-pk-hold-block"
                  style={{
                    padding: '6px 10px', borderRadius: 8,
                    background: 'rgba(251,113,133,0.10)',
                    border: '1px solid rgba(251,113,133,0.45)',
                    color: '#fca5a5', fontSize: 12, fontWeight: 700,
                  }}
                >
                  {activeHoldsCount} active hold{activeHoldsCount === 1 ? '' : 's'} — step advance blocked.
                </div>
              )}
              <button
                type="button"
                className="jbv2-row-btn"
                onClick={advance}
                disabled={busy || pk.current_step_index === null || activeHoldsCount > 0}
                data-testid="jbv2-pk-advance-btn"
              >
                <ChevronRight size={13} />{' '}
                {pk.current_step_index === null ? 'Procedure complete' : `Complete step ${pk.current_step_index + 1}`}
              </button>
              <button
                type="button"
                className="jbv2-row-btn jbv2-row-btn--muted"
                onClick={reset}
                disabled={busy || pk.completed_step_indices.length === 0}
                data-testid="jbv2-pk-reset-btn"
              >
                <RotateCcw size={12} /> Reset progress
              </button>
              <button
                type="button"
                className="jbv2-row-btn"
                onClick={() => {
                  setNameDraft('');
                  setStepsDraft('');
                  onLogged?.('place_keeping_procedure_set', { procedure_name: '', steps: [] });
                }}
                disabled={busy}
                data-testid="jbv2-pk-new-procedure-btn"
              >
                <Plus size={12} /> New procedure
              </button>
            </div>
          )}
        </div>
      )}
    </SectionShell>
  );
}
